import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Chip,
} from '@mui/material';
import { 
  Reddit as RedditIcon, 
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Tabs, Tab } from '@mui/material';
import { CDN_BASE_URL } from '../config';
import { AdminContextProvider } from './admin/AdminContext';
import SettingsTabs from './admin/SettingsTabs';
import HeaderBar from './admin/HeaderBar';
import ClearDataDialog from './admin/ClearDataDialog';
import MessageSnackbar from './admin/MessageSnackbar';
import FlairSheetSection from './admin/FlairSheetSection';
import PageDialogs from './admin/PageDialogs';
import TableSection from './admin/TableSection';
import { useAdminData } from './admin/hooks/useAdminData';
import { useCsvOperations } from './admin/hooks/useCsvOperations';
import { getSubredditFromUrl, navigateOrExternal, subredditLandingUrl } from '../utils/subreddit';
import { setSharedCookie } from '../utils/authRedirect';

type TabValue = 'flair-sheet' | 'class' | 'family' | 'genus' | 'flair' | 'image';

interface TableRow {
  [key: string]: any;
}

const AdminPage = (() => {
  const { subreddit: rawSubreddit } = useParams<{ subreddit: string }>();
  const navigate = useNavigate();
  const { user, logout, checkModeratorStatus, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingModerator, setIsCheckingModerator] = useState(false);
  /** Backend allows SITE_ADMIN_USERNAMES on any sub; mirror that here so UI matches API. */
  const [isSiteAdmin, setIsSiteAdmin] = useState(false);
  const modCheckKeyRef = useRef<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabValue | null>('flair-sheet');
  const [activeSettingsTab, setActiveSettingsTab] = useState<
    'csv' | 'images' | 'spritesheets' | 'settings' | 'user-flair' | 'grant-user-flair' | 'awards' | 'scheduler' | null
  >(null);
  
  // Nested navigation filters
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [selectedGenus, setSelectedGenus] = useState<string | null>(null);
  const [selectedFlair, setSelectedFlair] = useState<string | null>(null);
  
  // Move functionality state
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [itemToMove, setItemToMove] = useState<{ type: string; id: number; name: string; currentParent?: { parentType: string; parentName: string } } | null>(null);
  const [moveTargetOptions, setMoveTargetOptions] = useState<any[]>([]);
  const [selectedMoveTarget, setSelectedMoveTarget] = useState<string>('');
  const [isMoving, setIsMoving] = useState(false);
  
  // Add/Delete functionality state
  const [newItemData, setNewItemData] = useState<{ [key: string]: any }>({});
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isDeletingItem, setIsDeletingItem] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [parentOptions, setParentOptions] = useState<{ class?: any[]; family?: any[]; genus?: any[]; flair?: any[] }>({});
  
  // Table editing state (for class, family, genus, flair, image)
  const [editingRow, setEditingRow] = useState<TableRow | null>(null);
  const [originalEditingRow, setOriginalEditingRow] = useState<TableRow | null>(null); // Store original for comparison
  
  // Image upload state
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ 
    overall?: number; 
    currentBatch?: number; 
    totalBatches?: number; 
    batchProgress?: number;
  }>({});
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [uploadedImageNames, setUploadedImageNames] = useState<string[]>([]);
  const [showPushEmojisDialog, setShowPushEmojisDialog] = useState(false);
  const [isPushingEmojis, setIsPushingEmojis] = useState(false);
  
  // Purge unused images state
  const [unusedImages, setUnusedImages] = useState<string[]>([]);
  const [isLoadingUnusedImages, setIsLoadingUnusedImages] = useState(false);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [isPurgingImages, setIsPurgingImages] = useState(false);
  
  // Missing images state
  const [missingImages, setMissingImages] = useState<string[]>([]);
  const [isLoadingMissingImages, setIsLoadingMissingImages] = useState(false);
  const [showMissingImagesDialog, setShowMissingImagesDialog] = useState(false);

  // Reddit emoji audit vs master_sheet (Images tab) — not CDN / image table
  const [redditEmojiAudit, setRedditEmojiAudit] = useState<{
    master_sheet_flair_codes?: number;
    count_on_reddit?: number;
    matched_count?: number;
    orphan_count?: number;
    orphans?: string[];
    emojis_on_reddit?: string[];
  } | null>(null);
  const [showRedditEmojiPurgeDialog, setShowRedditEmojiPurgeDialog] = useState(false);
  const [isLoadingRedditEmojiAudit, setIsLoadingRedditEmojiAudit] = useState(false);
  const [isPurgingRedditEmojiOrphans, setIsPurgingRedditEmojiOrphans] = useState(false);

  // Force emoji update state (Images tab)
  const [forceEmojiImageNames, setForceEmojiImageNames] = useState('');
  const [isPushingForceEmojis, setIsPushingForceEmojis] = useState(false);

  // Crop image to sides (Images tab — under Force Emoji)
  const [cropToSidesOptions, setCropToSidesOptions] = useState<string[]>([]);
  const [cropToSidesSelected, setCropToSidesSelected] = useState<string | null>(null);
  const [cropToSidesCdnStem, setCropToSidesCdnStem] = useState<string | null>(null);
  const [cropToSidesPreviewBust, setCropToSidesPreviewBust] = useState(0);
  const [isLoadingCropToSidesOptions, setIsLoadingCropToSidesOptions] = useState(false);
  const [isResolvingCropToSidesStem, setIsResolvingCropToSidesStem] = useState(false);
  const [isCroppingToSides, setIsCroppingToSides] = useState(false);

  // Reddit OAuth state
  const [isAuthingReddit, setIsAuthingReddit] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Wiki/Reddit operations state
  const [isPushingWiki, setIsPushingWiki] = useState(false);
  const [wikiPushStatus, setWikiPushStatus] = useState<{
    flair?: { success: boolean; error?: string };
    inlineflair?: { success: boolean; error?: string };
    stylesheet?: { success: boolean; error?: string };
  } | null>(null);
  const [isFixingWikiPages, setIsFixingWikiPages] = useState(false);
  const [wikiFixStatus, setWikiFixStatus] = useState<{
    pages_fixed?: number;
    pages_checked?: number;
    errors?: string[];
    permission_warnings?: string[];
    message?: string;
  } | null>(null);
  const [isUpdatingDefaultFlair, setIsUpdatingDefaultFlair] = useState(false);
  const [updateDefaultFlairProgress, setUpdateDefaultFlairProgress] = useState<{
    isRunning?: boolean;
    progress?: Record<string, unknown> | null;
    result?: Record<string, unknown> | null;
  } | null>(null);
  const [isImportingModerators, setIsImportingModerators] = useState(false);
  const [isClearingData, setIsClearingData] = useState(false);
  const [clearDataDialogOpen, setClearDataDialogOpen] = useState(false);
  const [isSyncingSheets, setIsSyncingSheets] = useState(false);
  const [isGettingUserFlairs, setIsGettingUserFlairs] = useState(false);
  /** True while Reddit→DB sync runs; blocks applying stale list loads from overlapping GET /user-flairs fetches. */
  const userFlairRedditSyncInProgressRef = useRef(false);
  const [userFlairSyncProgress, setUserFlairSyncProgress] = useState<{
    isRunning?: boolean;
    db_user_flair_rows?: number | null;
    progress?: { phase?: string; fetched?: number; total?: number; written?: number; batch?: number } | null;
  } | null>(null);
  
  // Discord integration state
  const [discordConnected, setDiscordConnected] = useState(false);
  const [isConnectingDiscord, setIsConnectingDiscord] = useState(false);
  
  // Awards state
  const [awards, setAwards] = useState<any[]>([]);
  const [isLoadingAwards, setIsLoadingAwards] = useState(false);
  const [selectedAwardCode, setSelectedAwardCode] = useState<string | null>(null);
  const [newAwardUsername, setNewAwardUsername] = useState('');
  
  // User Flair Management state
  const [userFlairs, setUserFlairs] = useState<any[]>([]);
  const [isLoadingUserFlairs, setIsLoadingUserFlairs] = useState(false);
  
  // Scheduler state
  const [schedulerSettings, setSchedulerSettings] = useState<{
    enable_message_listener: boolean;
    enable_nightly_jobs: boolean;
    next_message_check: string | null;
    next_nightly_job: string | null;
  } | null>(null);
  const [isLoadingScheduler, setIsLoadingScheduler] = useState(false);
  const [isUpdatingScheduler, setIsUpdatingScheduler] = useState(false);
  const [subredditSettings, setSubredditSettings] = useState<{
    simplified_flair: boolean;
    default_logo_image_source_override: string | null;
  } | null>(null);
  const [isLoadingSubredditSettings, setIsLoadingSubredditSettings] = useState(false);
  const [isUpdatingSubredditSettings, setIsUpdatingSubredditSettings] = useState(false);
  const [nightlyJobHistory, setNightlyJobHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isRunningNightlyJobs, setIsRunningNightlyJobs] = useState(false);
  const [userFlairSearch, setUserFlairSearch] = useState('');
  
  // Reddit limits state
  const [redditLimits, setRedditLimits] = useState<{
    images: { 
      limit: number; 
      per_image_size_limit?: number;
      total_spritesheets_uploaded: number;
      total_spritesheets_registered: number;
      total_images_registered: number;
      spritesheet_images_count?: number;
      non_spritesheet_images_count?: number;
      usage_percent?: number;
      spritesheet_usage_percent?: number;
      non_spritesheet_usage_percent?: number;
      current: any[]; 
      over_limit: any[]; 
    };
    stylesheet: { 
      limit: number; 
      total_size: number;
      flair_css_size: number;
      non_flair_css_size?: number;
      total_usage_percent: number;
      flair_usage_percent: number;
      non_flair_usage_percent?: number;
    };
    emojis: { limit: number; current_count: number; usage_percent: number };
    default_flairs: { limit: number; current_count: number; usage_percent: number };
  } | null>(null);
  const [userFlairPage, setUserFlairPage] = useState(1);
  const [userFlairTotal, setUserFlairTotal] = useState(0);
  const [userFlairTotalPages, setUserFlairTotalPages] = useState(0);
  const [editingUserFlair, setEditingUserFlair] = useState<any | null>(null);
  const [validationResults, setValidationResults] = useState<any | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [fixQueueStatus, setFixQueueStatus] = useState<any | null>(null);
  const [enqueueProgress, setEnqueueProgress] = useState<any | null>(null);
  const [isDrainingFixes, setIsDrainingFixes] = useState(false);
  const [isPushingFlair, setIsPushingFlair] = useState<string | null>(null);
  
  // Subscription state
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  const [availableSubs, setAvailableSubs] = useState<string[]>([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [hasAuth, setHasAuth] = useState<boolean | null>(null);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [selectedParentSub, setSelectedParentSub] = useState('');
  
  const subredditFromHost = getSubredditFromUrl();
  const subreddit = (rawSubreddit?.toLowerCase() || subredditFromHost) || '';
  const canonicalSubredditName = rawSubreddit || subredditFromHost || '';

  /** Parent sub whose master_sheet we show when this sub subscribes for flair data (CDN + flair sheet read-only). */
  const masterSheetSourceSub =
    isSubscribed && subscriptions.length > 0 ? subscriptions[0].toLowerCase() : null;
  const flairSheetCdnSub =
    isSubscribed && subscriptions.length > 0 ? subscriptions[0].toLowerCase() : subreddit || '';

  // Messages
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning', text: string, details?: string } | null>(null);
  
  // Use custom hook for data fetching
  const {
    masterSheetData,
    isLoadingMasterSheet,
    masterSheetCacheKeyRef,
    masterSheetDataLengthRef,
    isLoadingMasterSheetRef,
    loadMasterSheet,
    tableData,
    isLoadingTable,
    tableDataCache,
    setTableData,
    setTableDataCache,
    loadTableData,
    setMasterSheetCacheKey,
    syncRefs,
  } = useAdminData({ 
    subreddit: subreddit ?? null,
    masterSheetSourceSub,
    user, 
    setMessage 
  });

  // CSV Import operations (using custom hook) - must be after useAdminData
  const {
    selectedFile,
    setSelectedFile,
    selectedHistoryFiles,
    setSelectedHistoryFiles,
    isUploading,
    isImportingHistory,
    selectedGenusCsvFile,
    setSelectedGenusCsvFile,
    isImportingGenusChanges,
    selectedFadingCsvFile,
    setSelectedFadingCsvFile,
    isImportingFading,
    handleImportHistory,
    handleImportGenusChanges,
    handleImportFading,
    handleSyncMasterSheetFromImages,
    handleRebuildMasterSheetFromBaseTables,
    isSyncingMasterSheet,
    handleAppendCsvUpload,
    fadedAssetsClassInput,
    setFadedAssetsClassInput,
    isGeneratingFadedAssets,
    handleGenerateFadedAssetsForClass,
    fadeGenusInput,
    setFadeGenusInput,
    fadeGenusIncludeBandwagons,
    setFadeGenusIncludeBandwagons,
    isFadingGenus,
    fadeGenusPreview,
    handleFadeGenus,
  } = useCsvOperations({
    subreddit: subreddit ?? null,
    user,
    setMessage,
    loadMasterSheet,
    loadTableData,
    setTableDataCache,
    setMasterSheetCacheKey,
    activeTab,
    selectedClass,
    selectedFamily,
    selectedGenus,
    selectedFlair,
  });

  // Sync refs with state to keep them up to date (using hook's syncRefs)
  useEffect(() => {
    syncRefs();
  }, [syncRefs]);

  // Mark subscription as loading before useEffects run so flair sheet waits for parent vs local master_sheet
  useLayoutEffect(() => {
    if (subreddit && user) {
      setIsLoadingSubscriptions(true);
    } else {
      setIsLoadingSubscriptions(false);
    }
  }, [subreddit, user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      modCheckKeyRef.current = null;
      setIsLoading(false);
      setIsSiteAdmin(false);
      return;
    }
    if (!subreddit) {
      setIsLoading(false);
      return;
    }
    const checkKey = `${user.id}:${subreddit}`;
    if (modCheckKeyRef.current === checkKey) return;
    modCheckKeyRef.current = checkKey;
    setIsLoading(true);
    checkModeratorStatusForSubreddit();
  }, [authLoading, user?.id, subreddit]);

  useEffect(() => {
    if (!subreddit || !user) {
      return;
    }

    // Wait until subscription status is known so we know whether to load parent or local master_sheet
    if (isLoadingSubscriptions) {
      return;
    }
    
    // Prevent running if already loading to avoid infinite loops
    if (isLoadingMasterSheetRef.current) {
      return;
    }

    const fetchSubMaster = (masterSheetSourceSub || subreddit).toLowerCase();
    
    // For flair-sheet (default tab), load immediately without delay for instant display
    if (activeTab === 'flair-sheet') {
      // Only reload if source sub changed or we don't have data
      // Use refs to check without causing re-renders
      const currentCacheKey = masterSheetCacheKeyRef.current;
      const currentDataLength = masterSheetDataLengthRef.current;
      if (currentCacheKey !== fetchSubMaster || currentDataLength === 0) {
        // Use quick load for initial page load (shows first 200 rows instantly)
        const isInitialLoad = currentDataLength === 0;
        loadMasterSheet(false, isInitialLoad);
      }
    } else if (activeTab && ['class', 'family', 'genus', 'flair', 'image'].includes(activeTab)) {
      // Small delay for other tabs to ensure user is fully loaded
      const timer = setTimeout(() => {
        // Apply filters based on active tab and selected filters
        const filters: { class?: string; family?: string; genus?: string; flair?: string } = {};
        if (activeTab === 'family' && selectedClass) filters.class = selectedClass;
        if (activeTab === 'genus' && selectedFamily) filters.family = selectedFamily;
        if (activeTab === 'flair' && selectedGenus) filters.genus = selectedGenus;
        if (activeTab === 'image' && selectedFlair) filters.flair = selectedFlair;
        else if (activeTab === 'image' && selectedGenus) filters.genus = selectedGenus;
        
        // loadTableData already handles caching internally
        loadTableData(activeTab, filters);
        
        // Load parent options for add form (can be async, non-blocking)
        if (activeTab === 'family') {
          loadParentOptions('class');
        } else if (activeTab === 'genus') {
          loadParentOptions('family');
        } else if (activeTab === 'flair') {
          loadParentOptions('genus');
        } else if (activeTab === 'image') {
          loadParentOptions('flair');
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    // Note: We intentionally don't include loadMasterSheet in deps to prevent infinite loops
    // The function is stable (only depends on subreddit) and we check refs before calling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, subreddit, user?.id, selectedClass, selectedFamily, selectedGenus, selectedFlair, isLoadingSubscriptions, masterSheetSourceSub]);

  // Handle Reddit OAuth success redirect
  useEffect(() => {
    const authSuccess = searchParams.get('auth');
    if (authSuccess === 'success' && subreddit) {
      setActiveSettingsTab('settings');
      setActiveTab(null);
      setMessage({
        type: 'success',
        text: `Reddit authentication successful! Credentials saved for /r/${subreddit}. You can now use "Push to Reddit" on spritesheets.`,
      });
      setSearchParams({});
    }
  }, [searchParams, subreddit]);

  // Handle Discord OAuth success redirect
  useEffect(() => {
    const discordConnectedParam = searchParams.get('discord_connected');
    if (discordConnectedParam === 'true' && subreddit) {
      setDiscordConnected(true);
      setActiveSettingsTab('settings');
      setActiveTab(null);
      setMessage({
        type: 'success',
        text: 'Discord account connected! Your moderator roles should be assigned automatically in Discord.',
      });
      setSearchParams({});
    }
  }, [searchParams, subreddit]);

  // Load awards when awards tab is selected
  useEffect(() => {
    if (activeSettingsTab === 'awards' && subreddit && user) {
      loadAwards();
    }
  }, [activeSettingsTab, subreddit, user]);

  // Load user flairs when user-flair tab is selected (skip while Reddit sync is running — avoid stale full-table updates)
  useEffect(() => {
    if (activeSettingsTab === 'user-flair' && subreddit && user && !isGettingUserFlairs) {
      loadUserFlairs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSettingsTab, subreddit, user, userFlairPage, userFlairSearch, isGettingUserFlairs]);

  // Load scheduler settings when scheduler tab is selected
  useEffect(() => {
    if (activeSettingsTab === 'scheduler' && subreddit && user) {
      loadSchedulerSettings();
      loadNightlyJobHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSettingsTab, subreddit, user]);

  useEffect(() => {
    if (activeSettingsTab === 'settings' && subreddit && user) {
      loadSubredditSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSettingsTab, subreddit, user]);
  
  const loadNightlyJobHistory = async () => {
    if (!subreddit || !user) return;
    setIsLoadingHistory(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/nightly-job-history`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${user.username}`
        }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        const history = Array.isArray(data.history) ? data.history : [];
        setNightlyJobHistory(history);
      } else {
        console.error('Failed to load nightly job history:', data);
        console.error('Error details:', data.error, data.details);
        setNightlyJobHistory([]);
      }
    } catch (error) {
      console.error('Error loading nightly job history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };
  
  // Load Reddit limits and job history when component mounts or subreddit changes
  useEffect(() => {
    if (subreddit && user) {
      loadRedditLimits();
      loadNightlyJobHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subreddit, user]);
  
  const loadRedditLimits = async () => {
    if (!subreddit || !user) return;
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/reddit-limits`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${user.username}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error loading Reddit limits:', response.status, errorText);
        // Don't show error to user if endpoint doesn't exist yet (404)
        if (response.status === 404) {
          console.warn('Reddit limits endpoint not found - server may need restart');
        }
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setRedditLimits(data.limits);
      }
    } catch (error: any) {
      console.error('Error loading Reddit limits:', error);
      // Silently fail - limits are optional
    }
  };
  
  const handleRunNightlyJobs = async () => {
    if (!subreddit) return;
    setIsRunningNightlyJobs(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/run-nightly-jobs`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${user?.username || ''}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: data.message || 'Nightly jobs started. Check server logs for progress.' });
        // Refresh history immediately and then at intervals to catch the job as it progresses
        loadNightlyJobHistory();
        setTimeout(() => loadNightlyJobHistory(), 2000);
        setTimeout(() => loadNightlyJobHistory(), 5000);
        setTimeout(() => loadNightlyJobHistory(), 10000);
        setTimeout(() => loadNightlyJobHistory(), 20000);
        setTimeout(() => loadNightlyJobHistory(), 30000);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to start nightly jobs' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to start nightly jobs' });
    } finally {
      setIsRunningNightlyJobs(false);
    }
  };

  const loadSchedulerSettings = async () => {
    if (!subreddit || !user) return;
    setIsLoadingScheduler(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/scheduler`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSchedulerSettings(data);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to load scheduler settings' }));
        setMessage({ type: 'error', text: errorData.error || 'Failed to load scheduler settings' });
      }
    } catch (error: any) {
      console.error('Error loading scheduler settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load scheduler settings' });
    } finally {
      setIsLoadingScheduler(false);
    }
  };

  const loadSubredditSettings = async () => {
    if (!subreddit || !user) return;
    setIsLoadingSubredditSettings(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/subreddit-settings`, {
        headers: { Authorization: `Bearer ${user.username}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSubredditSettings({
          simplified_flair: !!data.simplified_flair,
          default_logo_image_source_override:
            data.default_logo_image_source_override != null
              ? String(data.default_logo_image_source_override)
              : null,
        });
      } else {
        setSubredditSettings({ simplified_flair: false, default_logo_image_source_override: null });
      }
    } catch (e) {
      console.error('Error loading subreddit settings:', e);
      setSubredditSettings({ simplified_flair: false, default_logo_image_source_override: null });
    } finally {
      setIsLoadingSubredditSettings(false);
    }
  };

  const handleUpdateSubredditSettings = async (
    field: 'simplified_flair' | 'default_logo_image_source_override',
    value: boolean | string | null
  ) => {
    if (!subreddit || !user) return;
    setIsUpdatingSubredditSettings(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/subreddit-settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${user.username}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: value }),
      });
      if (response.ok) {
        const data = await response.json();
        setSubredditSettings((prev) => ({
          simplified_flair:
            data.simplified_flair !== undefined ? !!data.simplified_flair : prev?.simplified_flair ?? false,
          default_logo_image_source_override:
            data.default_logo_image_source_override !== undefined
              ? data.default_logo_image_source_override != null
                ? String(data.default_logo_image_source_override)
                : null
              : prev?.default_logo_image_source_override ?? null,
        }));
        setMessage({ type: 'success', text: data.message || 'Settings saved' });
      } else {
        const err = await response.json().catch(() => ({ error: 'Failed to save' }));
        setMessage({ type: 'error', text: err.error || 'Failed to save subreddit settings' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to save subreddit settings' });
    } finally {
      setIsUpdatingSubredditSettings(false);
    }
  };

  const handleUpdateSchedulerSettings = async (field: 'enable_message_listener' | 'enable_nightly_jobs', value: boolean) => {
    if (!subreddit || !user) return;
    setIsUpdatingScheduler(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/scheduler`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${user.username}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: value }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setSchedulerSettings(data);
        setMessage({ type: 'success', text: data.message || 'Scheduler settings updated successfully' });
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update scheduler settings' }));
        setMessage({ type: 'error', text: errorData.error || 'Failed to update scheduler settings' });
      }
    } catch (error: any) {
      console.error('Error updating scheduler settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update scheduler settings' });
    } finally {
      setIsUpdatingScheduler(false);
    }
  };

  // Load subscription status and auth status
  useEffect(() => {
    if (subreddit && user) {
      loadSubscriptionStatus();
      loadAuthStatus();
    }
  }, [subreddit, user]);

  const loadSubscriptionStatus = async () => {
    if (!subreddit || !user) return;
    setIsLoadingSubscriptions(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const [subResponse, availableResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/moderator/${subreddit}/is-subscribed`, {
          headers: { 'Authorization': `Bearer ${user.username}` },
        }),
        fetch(`${API_BASE_URL}/moderator/${subreddit}/available-subs`, {
          headers: { 'Authorization': `Bearer ${user.username}` },
        }),
      ]);
      
      if (subResponse.ok) {
        const subData = await subResponse.json();
        const subscribed = Boolean(subData.is_subscribed);
        setIsSubscribed(subscribed);
        setSubscriptions(subData.parent_subs || []);
        if (subscribed) {
          setActiveTab('flair-sheet');
        }
      }
      
      if (availableResponse.ok) {
        const availableData = await availableResponse.json();
        setAvailableSubs(availableData.available_subs || []);
      }
    } catch (error) {
      console.error('Error loading subscription status:', error);
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };

  const loadAuthStatus = async () => {
    if (!subreddit || !user) return;
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/check-auth`, {
        headers: { 'Authorization': `Bearer ${user.username}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setHasAuth(data.has_auth || false);
      }
    } catch (error) {
      console.error('Error loading auth status:', error);
    }
  };

  const handleSubscribe = async () => {
    if (!subreddit || !user || !selectedParentSub) return;
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.username}`,
        },
        body: JSON.stringify({ parent_sub: selectedParentSub }),
      });
      
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: data.message || 'Subscribed successfully' });
        setSelectedParentSub('');
        loadSubscriptionStatus();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to subscribe' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to subscribe: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const handleUnsubscribe = async (parentSub: string) => {
    if (!subreddit || !user) return;
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/subscriptions/${parentSub}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${user.username}` },
      });
      
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: data.message || 'Unsubscribed successfully' });
        loadSubscriptionStatus();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to unsubscribe' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to unsubscribe: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const loadAwards = async () => {
    if (!subreddit || !user) return;
    setIsLoadingAwards(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/awards`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAwards(data.awards || []);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: `Failed to load awards: ${errorData.error || 'Unknown error'}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to load awards: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsLoadingAwards(false);
    }
  };

  const handleAssignAward = async (flairCode: string, usernameInput: string) => {
    if (!subreddit || !user) return;
    // Split on comma (including fullwidth ，) and newlines; trim each
    const usernames = usernameInput.split(/[,，\n]+/).map((u) => u.trim()).filter(Boolean);
    if (usernames.length === 0) return;
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/awards/${flairCode}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.username}`,
        },
        body: JSON.stringify({ usernames }),
      });
      
      const data = await response.json();
      if (response.ok) {
        const assigned = data.assigned ?? 0;
        const skipped = data.skipped ?? 0;
        let msg = data.message || 'Done';
        if (assigned > 0) msg += `. Assigned: ${assigned}`;
        if (skipped > 0) msg += `. Skipped (already had): ${skipped}`;
        setMessage({ type: 'success', text: msg });
        loadAwards();
        setNewAwardUsername('');
      } else {
        const errorMsg = data.error || data.details || 'Failed to assign award';
        setMessage({ type: 'error', text: `Failed to assign award: ${errorMsg}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to assign award: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const handleRemoveAward = async (flairCode: string, username: string) => {
    if (!subreddit || !user) return;
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/awards/${flairCode}/users/${username}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: `Award removed from ${username} successfully!` });
        loadAwards();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove award');
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to remove award: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const checkModeratorStatusForSubreddit = async () => {
    if (!subreddit || !user) return;
    setIsCheckingModerator(true);
    setIsSiteAdmin(false);
    try {
      const { API_BASE_URL } = await import('../config');
      const saRes = await fetch(`${API_BASE_URL}/site-admin/me`, {
        headers: { Authorization: `Bearer ${user.username}` },
      });
      setIsSiteAdmin(saRes.ok);
      await checkModeratorStatus(subreddit);
    } catch (error) {
      console.error('Error checking moderator status:', error);
    } finally {
      setIsCheckingModerator(false);
      setIsLoading(false);
    }
  };


  const loadParentOptions = async (parentType: 'class' | 'family' | 'genus' | 'flair') => {
    if (!subreddit || !user) return;
    
    try {
      const { API_BASE_URL } = await import('../config');
      const params = new URLSearchParams({ subreddit });
      
      const response = await fetch(`${API_BASE_URL}/admin/tables/${parentType}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setParentOptions({ ...parentOptions, [parentType]: data.rows || [] });
      }
    } catch (error) {
      console.error(`Error loading ${parentType} options:`, error);
    }
  };
  // Store field metadata separately - only recalculate when editing starts (not on value changes)
  const [editFieldMetadata, setEditFieldMetadata] = useState<Array<{key: string; label: string}>>([]);
  
  const handleTableEdit = useCallback((row: TableRow) => {
    setIsSaving(false); // Reset saving state when opening edit dialog
    setEditingRow({ ...row });
    setOriginalEditingRow({ ...row }); // Store original for cache invalidation
    // Update field metadata when edit starts (only once) - compute labels here
    const keys = Object.keys(row).filter(key => key !== 'id' && !key.includes('_id'));
    setEditFieldMetadata(keys.map(key => ({
      key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    })));
  }, []);

  // Define minimum widths for columns that tend to have long text - memoized at component level
  // MUST be defined before any conditional returns to follow React hooks rules
  const getColumnWidth = useCallback((col: string) => {
    const longTextColumns = ['flair1', 'flair2', 'flair_text', 'image_source', 'flair_code', 'flair_name', 'image_name'];
    if (longTextColumns.some(l => col.toLowerCase().includes(l))) {
      return { minWidth: 200 };
    }
    return { minWidth: 100 };
  }, []);

  const handleMoveItem = async (itemType: string, itemId: number, itemName: string, currentParentInfo?: { parentType: string; parentName: string }) => {
    if (!subreddit || !user) return;
    
    setItemToMove({ 
      type: itemType, 
      id: itemId, 
      name: itemName,
      currentParent: currentParentInfo // Store old parent info for cache clearing
    });
    
    // Determine target parent type and load options
    let targetParentType = '';
    if (itemType === 'flair') {
      targetParentType = 'genus';
    } else if (itemType === 'genus') {
      targetParentType = 'family';
    } else if (itemType === 'family') {
      targetParentType = 'class';
    }
    
    if (!targetParentType) {
      setMessage({ type: 'error', text: 'Invalid item type for move' });
      return;
    }
    
    // Load target options (allow moving to current parent to reset sheet position)
    try {
      const { API_BASE_URL } = await import('../config');
      
      // Load all target options with filters
      const filters: { class?: string; family?: string } = {};
      if (targetParentType === 'family' && selectedClass) filters.class = selectedClass;
      
      const params = new URLSearchParams({ subreddit });
      if (filters.class) params.append('class', filters.class);
      
      const response = await fetch(`${API_BASE_URL}/admin/tables/${targetParentType}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Allow moving to current parent (to reset sheet position)
        setMoveTargetOptions(data.rows || []);
        setMoveDialogOpen(true);
      } else {
        setMessage({ type: 'error', text: 'Failed to load target options' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const handlePushEmojisAfterUpload = async () => {
    if (!subreddit || !user || uploadedImageNames.length === 0) return;
    
    setIsPushingEmojis(true);
    try {
      const { API_BASE_URL } = await import('../config');
      
      // Normalize image names to lowercase for consistent database lookup
      // The backend uses case-insensitive matching, but normalizing here ensures consistency
      const normalizedImageNames = uploadedImageNames.map(name => name.toLowerCase().trim()).filter(Boolean);
      
      if (normalizedImageNames.length === 0) {
        setMessage({
        type: 'error',
          text: 'No valid image names to push',
      });
      return;
    }

      // Push emojis for each uploaded image
      // We need to find which class each image belongs to from master_sheet
      // For now, push all emojis for all classes that contain these images
      const url = `${API_BASE_URL}/moderator/${subreddit}/push-emojis-by-names`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.username}`,
        },
        body: JSON.stringify({
          imageNames: normalizedImageNames,
        }),
      });

      const responseText = await response.text();
      let postData: Record<string, unknown> = {};
      try {
        postData = responseText ? JSON.parse(responseText) : {};
      } catch {
        setMessage({
          type: 'error',
          text: `Server returned non-JSON (${response.status}). ${responseText ? responseText.slice(0, 200) : ''}`,
        });
        return;
      }

      if (response.status === 409) {
        setMessage({
          type: 'warning',
          text: (postData.details as string) || 'Emoji push already running. Waiting for it to finish…',
        });
      } else if (!response.ok && response.status !== 202) {
        console.error('Push emojis error response:', response.status, responseText);
        setMessage({
          type: 'error',
          text: (postData.error as string) || (postData.details as string) || `Failed to push emojis (${response.status})`,
        });
        return;
      }

      const { pollPushEmojisByNamesUntilDone } = await import('../utils/pushEmojisByNames');
      const shouldPoll =
        response.status === 202 ||
        response.status === 409 ||
        postData.started === true ||
        postData.success === true;
      let finalResult = null;
      if (shouldPoll) {
        finalResult = await pollPushEmojisByNamesUntilDone(API_BASE_URL, subreddit, user.username, {
          onProgress: (p) => {
            if (p?.phase === 'pushing' && p.totalClasses && p.currentClass) {
              setMessage({
                type: 'success',
                text: `Pushing emojis… class ${p.currentIndex ?? '?'}/${p.totalClasses} (${p.currentClass})`,
              });
            }
          },
        });
      }

      if (finalResult?.success === false) {
        setMessage({
          type: 'error',
          text: (finalResult.error as string) || 'Emoji push failed',
        });
        return;
      }
      if (finalResult != null) {
        const uploaded = finalResult.emojis_uploaded ?? 0;
        const errs = finalResult.errors?.length ? ` (${finalResult.errors.length} class warning(s); check message details)` : '';
        let extra = '';
        try {
          const cr = await fetch(
            `${API_BASE_URL}/moderator/${subreddit}/classes-for-images?names=${encodeURIComponent(normalizedImageNames.join(','))}`,
            { headers: { Authorization: `Bearer ${user.username}` } }
          );
          if (cr.ok) {
            const cd = await cr.json();
            const classes = (cd.classes || []) as string[];
            if (classes.length > 0) {
              const pr = await fetch(`${API_BASE_URL}/moderator/${subreddit}/post-emoji-refresh`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${user.username}`,
                },
                body: JSON.stringify({ classNames: classes }),
              });
              const prJson = (await pr.json().catch(() => ({}))) as { error?: string };
              if (pr.ok) {
                extra = ` Spritesheets + stylesheet updated (${classes.length} class${classes.length !== 1 ? 'es' : ''}).`;
              } else {
                extra = ` (spritesheet/wiki refresh failed: ${prJson.error || pr.status})`;
              }
            }
          }
        } catch (e) {
          console.error('post-emoji-refresh:', e);
          extra = ' (spritesheet refresh could not run; use Spritesheets tab or Settings → Push).';
        }
        setMessage({
          type: finalResult.errors?.length ? 'warning' : 'success',
          text: `Successfully pushed ${uploaded} emoji${uploaded !== 1 ? 's' : ''} to Reddit!${errs}${extra}`,
          details: finalResult.errors?.length ? finalResult.errors.join('\n') : undefined,
        });
        setShowPushEmojisDialog(false);
        setUploadedImageNames([]);
        setSelectedImages([]);
        return;
      }

      if (!shouldPoll && response.ok) {
        const data = postData as { emojis_uploaded?: number };
        setMessage({
          type: 'success',
          text: `Successfully pushed ${data.emojis_uploaded || 0} emojis to Reddit!`,
        });
        setShowPushEmojisDialog(false);
        setUploadedImageNames([]);
        setSelectedImages([]);
      } else if (finalResult === null && shouldPoll) {
        setMessage({
          type: 'warning',
          text: 'Emoji push may still be running on the server (stopped waiting). Check push-emojis-by-names-progress or server logs.',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error pushing emojis: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsPushingEmojis(false);
      setIsUploadingImages(false);
    }
  };

  const handleForceEmojiUpdate = async () => {
    if (!subreddit || !user) return;
    const names = forceEmojiImageNames
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean);
    if (names.length === 0) {
      setMessage({ type: 'error', text: 'Enter at least one image name (comma-separated)' });
      return;
    }
    setIsPushingForceEmojis(true);
    setMessage(null);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/push-emojis-by-names`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.username}`,
        },
        body: JSON.stringify({ imageNames: names }),
      });
      const responseText = await response.text();
      let postData: Record<string, unknown> = {};
      try {
        postData = responseText ? JSON.parse(responseText) : {};
      } catch {
        setMessage({
          type: 'error',
          text: `Server returned non-JSON (${response.status}). ${responseText ? responseText.slice(0, 200) : ''}`,
        });
        return;
      }

      if (response.status === 409) {
        setMessage({
          type: 'warning',
          text: (postData.details as string) || 'Emoji push already running. Waiting…',
        });
      } else if (!response.ok && response.status !== 202) {
        setMessage({
          type: 'error',
          text: (postData.error as string) || (postData.details as string) || 'Failed to push emojis',
        });
        return;
      }

      const { pollPushEmojisByNamesUntilDone } = await import('../utils/pushEmojisByNames');
      const shouldPoll =
        response.status === 202 ||
        response.status === 409 ||
        postData.started === true ||
        postData.success === true;
      let finalResult = null;
      if (shouldPoll) {
        finalResult = await pollPushEmojisByNamesUntilDone(API_BASE_URL, subreddit, user.username, {
          onProgress: (p) => {
            if (p?.phase === 'pushing' && p.totalClasses && p.currentClass) {
              setMessage({
                type: 'success',
                text: `Pushing emojis… ${p.currentIndex ?? '?'}/${p.totalClasses} (${p.currentClass})`,
              });
            }
          },
        });
      }

      if (finalResult?.success === false) {
        setMessage({
          type: 'error',
          text: (finalResult.error as string) || 'Emoji push failed',
        });
        return;
      }
      if (finalResult != null) {
        const n = finalResult.emojis_uploaded ?? 0;
        setMessage({
          type: finalResult.errors?.length ? 'warning' : 'success',
          text: `Successfully pushed ${n} emoji${n !== 1 ? 's' : ''} to Reddit`,
          details: finalResult.errors?.length ? finalResult.errors.join('\n') : undefined,
        });
        setForceEmojiImageNames('');
        return;
      }
      if (!shouldPoll && response.ok) {
        const data = postData as { emojis_uploaded?: number };
        setMessage({
          type: 'success',
          text: `Successfully pushed ${data.emojis_uploaded || 0} emoji${(data.emojis_uploaded || 0) !== 1 ? 's' : ''} to Reddit`,
        });
        setForceEmojiImageNames('');
      } else if (shouldPoll && finalResult === null) {
        setMessage({
          type: 'warning',
          text: 'Emoji push may still be running on the server (stopped waiting). Check logs or try again later.',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Error pushing emojis: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsPushingForceEmojis(false);
    }
  };

  const handleLoadCropToSidesOptions = async () => {
    if (!subreddit || !user) return;
    setIsLoadingCropToSidesOptions(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/crop-image-options`, {
        headers: { Authorization: `Bearer ${user.username}` },
      });
      const data = await response.json();
      if (!response.ok) {
        setMessage({ type: 'error', text: data.error || 'Failed to load image list for crop' });
        return;
      }
      setCropToSidesOptions(data.imageNames || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to load crop options: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsLoadingCropToSidesOptions(false);
    }
  };

  const handleCropToSidesSelectionChange = async (name: string | null) => {
    setCropToSidesSelected(name);
    setCropToSidesCdnStem(null);
    if (!name || !subreddit || !user) return;
    setIsResolvingCropToSidesStem(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(
        `${API_BASE_URL}/moderator/${subreddit}/crop-image-resolve?${new URLSearchParams({ imageName: name })}`,
        { headers: { Authorization: `Bearer ${user.username}` } }
      );
      const data = await response.json();
      if (!response.ok) {
        setMessage({
          type: 'error',
          text: data.error || data.details || 'Could not resolve CDN image for preview',
        });
        return;
      }
      setCropToSidesCdnStem(data.cdnStem || null);
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Resolve failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsResolvingCropToSidesStem(false);
    }
  };

  const handleCropImageToSidesConfirm = async () => {
    if (!subreddit || !user || !cropToSidesSelected) return;
    setIsCroppingToSides(true);
    setMessage(null);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/crop-image-to-sides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.username}`,
        },
        body: JSON.stringify({ imageName: cropToSidesSelected }),
      });
      const responseText = await response.text();
      let data: Record<string, unknown> = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        setMessage({
          type: 'error',
          text: `Server returned non-JSON (${response.status}). ${responseText ? responseText.slice(0, 200) : ''}`,
        });
        return;
      }
      if (!response.ok) {
        setMessage({
          type: 'error',
          text: (data.error as string) || (data.details as string) || `Crop request failed (${response.status})`,
        });
        return;
      }
      if (data.cropped === false) {
        setMessage({
          type: 'success',
          text: (data.message as string) || 'No crop needed; image already touches all sides.',
        });
        return;
      }
      setCropToSidesPreviewBust((b) => b + 1);
      const emojiPush = data.emojiPush as Record<string, unknown> | undefined;
      if (emojiPush?.started === true) {
        const { pollPushEmojisByNamesUntilDone } = await import('../utils/pushEmojisByNames');
        setMessage({
          type: 'success',
          text: 'Image cropped and CDN sizes updated. Pushing emojis to Reddit…',
        });
        const finalResult = await pollPushEmojisByNamesUntilDone(API_BASE_URL, subreddit, user.username, {
          onProgress: (p) => {
            if (p?.phase === 'pushing' && p.totalClasses && p.currentClass) {
              setMessage({
                type: 'success',
                text: `Cropped. Pushing emojis… ${p.currentIndex ?? '?'}/${p.totalClasses} (${p.currentClass})`,
              });
            }
          },
        });
        if (finalResult?.success === false) {
          setMessage({
            type: 'warning',
            text: `CDN updated, but emoji push reported an error: ${(finalResult.error as string) || 'unknown'}`,
          });
          return;
        }
        if (finalResult != null) {
          const n = finalResult.emojis_uploaded ?? 0;
          setMessage({
            type: finalResult.errors?.length ? 'warning' : 'success',
            text: `Crop complete. Pushed ${n} emoji${n !== 1 ? 's' : ''} to Reddit (spritesheets / stylesheets updated per class).`,
            details: finalResult.errors?.length ? finalResult.errors.join('\n') : undefined,
          });
          return;
        }
        setMessage({
          type: 'warning',
          text: 'CDN updated. Emoji push may still be running — check push-emojis-by-names-progress or try Force Emoji Update.',
        });
        return;
      }
      const errBody = emojiPush as { error?: string; details?: string } | undefined;
      setMessage({
        type: 'warning',
        text: `Image cropped and CDN updated, but emoji push did not start: ${errBody?.error || errBody?.details || 'unknown reason'}. You can use Force Emoji Update for this image.`,
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Crop error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsCroppingToSides(false);
    }
  };

  const handleAddItem = async (tableName: string) => {
    if (!subreddit || !user || isSubscribed) return;
    
    setIsAddingItem(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const itemData: { [key: string]: any } = { ...newItemData, subreddit };
      delete itemData.imageFile;

      if (tableName === 'class') {
        itemData.flair_width = itemData.flair_width != null ? Number(itemData.flair_width) : 60;
        itemData.flair_height = itemData.flair_height != null ? Number(itemData.flair_height) : 60;
      }

      const afterAddSuccess = () => {
        setMessage({ type: 'success', text: `Successfully added ${tableName}` });
        setNewItemData({});

        const filters: { class?: string; family?: string; genus?: string; flair?: string } = {};
        if (selectedClass) filters.class = selectedClass;
        if (selectedFamily) filters.family = selectedFamily;
        if (selectedGenus) filters.genus = selectedGenus;
        if (selectedFlair) filters.flair = selectedFlair;

        const cacheKey = `${subreddit}_${tableName}_${JSON.stringify(filters)}`;
        setTableDataCache((prev) => {
          const newCache = { ...prev };
          delete newCache[cacheKey];
          return newCache;
        });

        if (tableName === 'genus' || tableName === 'family' || tableName === 'class') {
          setParentOptions((prev) => ({
            ...prev,
            [tableName]: undefined,
          }));
        }

        loadTableData(tableName, filters, true);
        loadMasterSheet(true);
      };

      // New image + file: create DB row first — POST .../import-images only uploads files already registered in `image`.
      if (tableName === 'image' && newItemData.imageFile) {
        const file = newItemData.imageFile;

        const createRes = await fetch(`${API_BASE_URL}/admin/tables/image`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.username}`,
          },
          body: JSON.stringify(itemData),
        });

        const createRaw = await createRes.text();
        let createData: { error?: string };
        try {
          createData = JSON.parse(createRaw) as { error?: string };
        } catch {
          throw new Error(
            createRaw.trimStart().startsWith('<!')
              ? `Create image failed (${createRes.status}). Got HTML instead of JSON — check API base URL.`
              : `Create image failed: ${createRaw.slice(0, 200)}`
          );
        }
        if (!createRes.ok) {
          throw new Error(createData.error || `Failed to create image row (${createRes.status})`);
        }

        const formData = new FormData();
        formData.append('images', file);

        const uploadRes = await fetch(`${API_BASE_URL}/moderator/${subreddit}/import-images`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${user.username}`,
          },
          body: formData,
        });

        const uploadRaw = await uploadRes.text();
        let uploadData: {
          error?: string;
          details?: string;
          results?: { uploaded?: { imageName?: string }[]; ignored?: { reason?: string }[] };
        };
        try {
          uploadData = JSON.parse(uploadRaw);
        } catch {
          throw new Error(
            uploadRaw.trimStart().startsWith('<!')
              ? `Upload failed (${uploadRes.status}). Got HTML instead of JSON — use /api/moderator/.../import-images.`
              : `Upload failed: ${uploadRaw.slice(0, 200)}`
          );
        }
        if (!uploadRes.ok) {
          throw new Error(uploadData.error || uploadData.details || `CDN upload failed (${uploadRes.status})`);
        }

        const uploaded = uploadData.results?.uploaded ?? [];
        if (uploaded.length === 0) {
          const ign = uploadData.results?.ignored?.[0];
          throw new Error(
            ign?.reason
              ? `CDN: ${ign.reason}`
              : 'No file was uploaded to CDN. Ensure the file base name matches Image Name in the database.'
          );
        }

        afterAddSuccess();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/tables/${tableName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.username}`,
        },
        body: JSON.stringify(itemData),
      });

      const raw = await response.text();
      let data: { error?: string };
      try {
        data = JSON.parse(raw) as { error?: string };
      } catch {
        throw new Error(
          raw.trimStart().startsWith('<!')
            ? `Request failed (${response.status}). Expected JSON; got HTML (wrong API path or proxy).`
            : raw.slice(0, 200)
        );
      }
      if (response.ok) {
        afterAddSuccess();
      } else {
        setMessage({ type: 'error', text: data.error || `Failed to add ${tableName}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsAddingItem(false);
    }
  };

  const handleDeleteItem = async (tableName: string, itemId: number) => {
    if (!subreddit || !user || isSubscribed) return;
    
    if (!confirm(`Are you sure you want to delete this ${tableName}? This action cannot be undone.`)) {
      return;
    }
    
    setIsDeletingItem(itemId);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/admin/tables/${tableName}/${itemId}?subreddit=${subreddit}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: `Successfully deleted ${tableName}` });
        
        // Clear cache for current view
        const filters: { class?: string; family?: string; genus?: string; flair?: string } = {};
        if (selectedClass) filters.class = selectedClass;
        if (selectedFamily) filters.family = selectedFamily;
        if (selectedGenus) filters.genus = selectedGenus;
        if (selectedFlair) filters.flair = selectedFlair;
        
        const cacheKey = `${subreddit}_${tableName}_${JSON.stringify(filters)}`;
        setTableDataCache(prev => {
          const newCache = { ...prev };
          delete newCache[cacheKey];
          return newCache;
        });
        
        // Clear parent options if this affects dropdowns
        if (tableName === 'genus' || tableName === 'family' || tableName === 'class') {
          setParentOptions(prev => ({
            ...prev,
            [tableName]: undefined // Force reload
          }));
        }
        
        // Reload table data
        loadTableData(tableName, filters, true);
        loadMasterSheet(true);
      } else {
        setMessage({ type: 'error', text: data.error || `Failed to delete ${tableName}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsDeletingItem(null);
    }
  };

  const handleConfirmMove = async () => {
    if (!itemToMove || !selectedMoveTarget || !subreddit || !user) return;
    
    setIsMoving(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const targetParentType = itemToMove.type === 'flair' ? 'genus' : itemToMove.type === 'genus' ? 'family' : 'class';
      const targetRow = moveTargetOptions.find((r: any) => String(r[`${targetParentType}_id`]) === selectedMoveTarget);
      
      if (!targetRow) {
        setMessage({ type: 'error', text: 'Target not found' });
        setIsMoving(false);
        return;
      }
      
      // Single item move
      const response = await fetch(`${API_BASE_URL}/admin/move-item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.username}`,
        },
        body: JSON.stringify({
          itemType: itemToMove.type,
          itemId: itemToMove.id,
          targetParentType: targetParentType,
          targetParentName: targetRow[`${targetParentType}_name`],
          subreddit: subreddit,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        setMessage({ type: 'error', text: data.error || 'Failed to move item' });
        return; // Exit early, finally block will handle setIsMoving(false)
      }
      setMessage({ type: 'success', text: `Moved ${itemToMove.type} successfully!` });
      
      // Clear cache for both old and new parent views
      const targetParentName = targetRow[`${targetParentType}_name`];
      
      // Clear cache for old parent (if we have that info)
      if (itemToMove.currentParent) {
        const oldFilters: { class?: string; family?: string; genus?: string } = {};
        if (itemToMove.currentParent.parentType === 'class') {
          oldFilters.class = itemToMove.currentParent.parentName;
        } else if (itemToMove.currentParent.parentType === 'family') {
          oldFilters.family = itemToMove.currentParent.parentName;
        } else if (itemToMove.currentParent.parentType === 'genus') {
          oldFilters.genus = itemToMove.currentParent.parentName;
        }
        
        // Determine which table to clear cache for
        let oldTableName = '';
        if (itemToMove.type === 'flair') {
          oldTableName = 'flair';
        } else if (itemToMove.type === 'genus') {
          oldTableName = 'genus';
        } else if (itemToMove.type === 'family') {
          oldTableName = 'family';
        }
        
        if (oldTableName) {
          const oldCacheKey = `${subreddit}_${oldTableName}_${JSON.stringify(oldFilters)}`;
          setTableDataCache(prev => {
            const newCache = { ...prev };
            delete newCache[oldCacheKey];
            return newCache;
          });
        }
      }
      
      // Clear cache for new parent
      const newFilters: { class?: string; family?: string; genus?: string } = {};
      if (targetParentType === 'class') {
        newFilters.class = targetParentName;
      } else if (targetParentType === 'family') {
        newFilters.family = targetParentName;
      } else if (targetParentType === 'genus') {
        newFilters.genus = targetParentName;
      }
      
      let newTableName = '';
      if (itemToMove.type === 'flair') {
        newTableName = 'flair';
      } else if (itemToMove.type === 'genus') {
        newTableName = 'genus';
      } else if (itemToMove.type === 'family') {
        newTableName = 'family';
      }
      
      if (newTableName) {
        const newCacheKey = `${subreddit}_${newTableName}_${JSON.stringify(newFilters)}`;
        setTableDataCache(prev => {
          const newCache = { ...prev };
          delete newCache[newCacheKey];
          return newCache;
        });
      }
      
      setMoveDialogOpen(false);
      setItemToMove(null);
      setSelectedMoveTarget('');
      
      // Force reload current table and master sheet
      if (activeTab) {
        const filters: { class?: string; family?: string; genus?: string } = {};
        if (selectedClass) filters.class = selectedClass;
        if (selectedFamily) filters.family = selectedFamily;
        if (selectedGenus) filters.genus = selectedGenus;
        loadTableData(activeTab, filters, true); // Force reload
      }
      loadMasterSheet(true); // Force reload
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsMoving(false);
    }
  };

  const handleInitiateRedditAuth = async () => {
    if (!subreddit || !user) {
      setMessage({ type: 'error', text: 'Please log in and select a subreddit' });
      return;
    }
    
    setIsAuthingReddit(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/auth-reddit/initiate`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.authUrl) {
          // Store state for callback on www (sessionStorage is per-origin; tenant → www loses it)
          if (data.state) {
            sessionStorage.setItem('reddit_flair_auth_state', data.state);
            setSharedCookie('reddit_flair_auth_state', data.state, 600);
          }
          // Redirect to Reddit OAuth
          window.location.href = data.authUrl;
        } else {
          throw new Error('No auth URL received');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initiate Reddit authentication');
      }
    } catch (error) {
      setIsAuthingReddit(false);
      setMessage({
        type: 'error',
        text: `Failed to initiate Reddit authentication: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleDiscordAuth = async () => {
    if (!subreddit || !user) return;
    setIsConnectingDiscord(true);

    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/discord-auth/initiate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authUrl) {
          // Store state for verification
          if (data.state) {
            sessionStorage.setItem('discord_auth_state', data.state);
            sessionStorage.setItem('discord_auth_subreddit', subreddit);
          }
          // Redirect to Discord OAuth
          window.location.href = data.authUrl;
        } else {
          throw new Error('No auth URL received');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initiate Discord authentication');
      }
    } catch (error) {
      setIsConnectingDiscord(false);
      setMessage({
        type: 'error',
        text: `Failed to connect Discord: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleFixWikiPages = async (page?: string) => {
    if (!subreddit || !user) return;
    setIsFixingWikiPages(true);
    setWikiFixStatus(null);

    try {
      const { API_BASE_URL } = await import('../config');
      const body: { page?: string } = {};
      const trimmed = (page || '').trim();
      if (trimmed) body.page = trimmed;
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/fix-wiki-pages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.ok) {
        setWikiFixStatus(data);
        if (data.pages_fixed > 0) {
          setMessage({ 
            type: 'success', 
            text: data.message || `Fixed ${data.pages_fixed} wiki page(s) with outdated inline flair references.` 
          });
        } else if (data.permission_warnings && data.permission_warnings.length > 0) {
          setMessage({ 
            type: 'warning', 
            text: data.message || `Could not access ${data.permission_warnings.length} wiki page(s). Reddit credentials may need 'wikiread' scope.` 
          });
        } else {
          setMessage({ 
            type: 'success', 
            text: data.message || `Checked ${data.pages_checked || 0} wiki page(s). All inline flair references are up to date.` 
          });
        }
      } else {
        const errorMsg = data.error || data.details || `HTTP ${response.status}`;
        const detailsMsg = data.details ? ` Details: ${JSON.stringify(data.details)}` : '';
        throw new Error(errorMsg + detailsMsg);
      }
    } catch (error) {
      console.error('Fix wiki pages exception:', error);
      setMessage({
        type: 'error',
        text: `Failed to fix wiki pages: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsFixingWikiPages(false);
    }
  };

  const handlePushWikiPages = async () => {
    if (!subreddit || !user) return;
    setIsPushingWiki(true);
    setWikiPushStatus(null);

    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/push-wiki-pages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
        const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response as JSON:', e);
        throw new Error(`Server returned non-JSON response: ${responseText.substring(0, 200)}`);
      }
      
      if (response.ok) {
        setWikiPushStatus(data.results || {});
        if (data.success) {
          setMessage({ type: 'success', text: 'Successfully pushed wiki pages and stylesheet to Reddit!' });
        } else {
          const errors = Object.entries(data.results || {})
            .filter(([_, r]: [string, any]) => !r.success)
            .map(([key, r]: [string, any]) => `${key}: ${r.error || 'Failed'}`)
            .join(', ');
          console.error('Push wiki pages partial failure:', errors);
          setMessage({ type: 'error', text: `Some operations failed: ${errors}` });
        }
      } else {
        const errorMsg = data.error || data.details || `HTTP ${response.status}`;
        const detailsMsg = data.details ? ` Details: ${JSON.stringify(data.details)}` : '';
        console.error('Push wiki pages error:', {
          status: response.status,
          error: data.error,
          details: data.details,
          fullData: data
        });
        throw new Error(errorMsg + detailsMsg);
      }
    } catch (error) {
      console.error('Push wiki pages exception:', error);
      setMessage({
        type: 'error',
        text: `Failed to push wiki pages: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsPushingWiki(false);
    }
  };

  const handleDownloadOldSheet = async () => {
    if (!subreddit || !user) return;
    
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/export-csv`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${subreddit}_export_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setMessage({ type: 'success', text: 'CSV exported successfully!' });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export CSV');
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to export CSV: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleUpdateDefaultFlair = async () => {
    if (!subreddit || !user) return;
    setIsUpdatingDefaultFlair(true);
    setUpdateDefaultFlairProgress(null);

    const { API_BASE_URL } = await import('../config');
    const pollProgress = async () => {
      const pr = await fetch(`${API_BASE_URL}/moderator/${subreddit}/update-default-flair-progress`, {
        headers: { Authorization: `Bearer ${user.username}` },
      });
      if (pr.ok) {
        const data = await pr.json();
        setUpdateDefaultFlairProgress(data);
        return data as {
          isRunning?: boolean;
          progress?: Record<string, unknown> | null;
          result?: Record<string, unknown> | null;
        };
      }
      return null;
    };

    const progressTimer = window.setInterval(() => {
      void pollProgress();
    }, 2000);

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    try {
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/update-default-flair`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user.username}`,
        },
      });

      const responseText = await response.text();
      let data: Record<string, unknown> = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (e) {
        console.error('Failed to parse response as JSON:', e);
        setMessage({
          type: 'error',
          text: `Server returned non-JSON: ${responseText.substring(0, 200)}`,
        });
        return;
      }

      if (response.status === 409) {
        setMessage({
          type: 'warning',
          text: (data.details as string) || 'Update default flair is already running. Showing progress…',
        });
      } else if (response.status === 202 || data.started === true) {
        await pollProgress();
      } else if (!response.ok) {
        setMessage({
          type: 'error',
          text: (data.error as string) || `HTTP ${response.status}`,
          details: data.details as string | undefined,
        });
        return;
      }

      let stall = 0;
      let iter = 0;
      const maxIter = 1800;
      while (iter < maxIter) {
        iter += 1;
        const p = await pollProgress();
        if (!p) {
          await sleep(2000);
          continue;
        }
        if (!p.isRunning) {
          const r = p.result as Record<string, unknown> | null | undefined;
          if (r) break;
          stall += 1;
          if (stall > 25) {
            setMessage({
              type: 'warning',
              text: 'Job finished but no result file yet. Check server logs or wait and refresh.',
            });
            break;
          }
        }
        await sleep(2000);
      }
      if (iter >= maxIter) {
        setMessage({
          type: 'warning',
          text: 'Stopped waiting after a long run. The job may still be finishing on the server — check logs or ps.',
        });
      }

      const final = await pollProgress();
      const r = final?.result as
        | { success?: boolean; error?: string; deleted?: number; created?: number; details?: unknown; traceback?: string }
        | undefined;
      if (r?.success) {
        setMessage({
          type: 'success',
          text: `Successfully updated default flair templates! Deleted: ${r.deleted ?? 0}, Created: ${r.created ?? 0}`,
        });
      } else if (r) {
        const errText = (r.error as string) || 'Update default flair failed';
        const det = r.details;
        const detailsStr = Array.isArray(det) ? det.join('\n') : typeof det === 'string' ? det : r.traceback;
        setMessage({
          type: 'error',
          text: errText,
          details: detailsStr,
        });
      }
    } catch (error) {
      console.error('Update default flair exception:', error);
      setMessage({
        type: 'error',
        text: `Failed to update default flair: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      window.clearInterval(progressTimer);
      setIsUpdatingDefaultFlair(false);
    }
  };

  const handleImportModerators = async () => {
    if (!subreddit || !user) return;
    setIsImportingModerators(true);
    
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/import-moderators`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({
          type: 'success',
          text: `Successfully rescraped sub info: ${data.imported} new moderators imported, ${data.total} total moderators`,
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to import moderators');
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to rescrape sub info: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
      } finally {
        setIsImportingModerators(false);
      }
    };

  const handleClearData = async () => {
    if (!subreddit || !user) return;
    setIsClearingData(true);
    setClearDataDialogOpen(false);
    
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/clear-data`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({
          type: 'success',
          text: `Successfully cleared all data for /r/${subreddit}. ${data.results ? `Deleted: ${data.results.master_sheet} master_sheet rows, ${data.results.class} classes, ${data.results.family} families, ${data.results.genus} genuses, ${data.results.flair} flairs, ${data.results.image} images.` : ''}`,
        });
        // Reload master sheet if we're on that tab
        if (activeTab === 'flair-sheet') {
          loadMasterSheet();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to clear data');
      }
    } catch (error) {
      setMessage({
          type: 'error',
        text: `Failed to clear data: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsClearingData(false);
    }
  };

  const handleSyncSheets = async () => {
    if (!subreddit || !user) return;
    setIsSyncingSheets(true);
    
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/admin/sync-sheets/${subreddit}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({
          type: 'success',
          text: data.message || `Successfully synced sheets for /r/${subreddit}`,
        });
        // Reload master sheet if we're on that tab
        if (activeTab === 'flair-sheet') {
          loadMasterSheet();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to sync sheets');
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to sync sheets: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsSyncingSheets(false);
    }
  };


  const handleGetUserFlairs = async () => {
    if (!subreddit || !user) return;
    userFlairRedditSyncInProgressRef.current = true;
    setIsGettingUserFlairs(true);
    setUserFlairSyncProgress(null);

    const { API_BASE_URL } = await import('../config');

    const pollProgress = async () => {
      const pr = await fetch(`${API_BASE_URL}/moderator/${subreddit}/get-user-flairs-progress`, {
        headers: { Authorization: `Bearer ${user.username}` },
      });
      if (pr.ok) {
        const data = await pr.json();
        setUserFlairSyncProgress(data);
        return data as {
          isRunning?: boolean;
          progress?: { phase?: string; error?: string };
          result?: Record<string, unknown> | null;
          db_user_flair_rows?: number | null;
        };
      }
      return null;
    };

    const progressTimer = window.setInterval(() => {
      void pollProgress();
    }, 2000);

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    try {
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/get-user-flairs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });

      const text = await response.text();
      let data: Record<string, unknown> = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        setMessage({
          type: 'error',
          text: `Failed to start user flair sync: invalid JSON (${response.status}). ${text ? text.slice(0, 300) : ''}`,
        });
        return;
      }

      if (response.status === 409) {
        setMessage({
          type: 'warning',
          text: (data.details as string) || 'A flair sync is already running. Waiting for it to finish…',
        });
      } else if (!response.ok && response.status !== 202) {
        const details = (data.details as string) || (data.error as string) || 'Unknown error';
        setMessage({ type: 'error', text: `Failed to start sync: ${details}` });
        return;
      }

      // 202 Accepted: job runs in background (avoids Cloudflare 524 on long HTTP waits). 409: join existing run.
      const shouldPoll =
        response.status === 202 ||
        response.status === 409 ||
        data.started === true ||
        data.success === true;
      if (shouldPoll) {
        let stallAfterIdle = 0;
        for (;;) {
          const last = await pollProgress();
          if (!last) {
            await sleep(2000);
            continue;
          }
          if (last.isRunning) {
            stallAfterIdle = 0;
            await sleep(2000);
            continue;
          }
          const r = last.result as Record<string, unknown> | null | undefined;
          if (r && r.success === true) {
            let msg = `Successfully updated user flairs! Users: ${r.users_updated}, History entries: ${r.history_entries}`;
            if (r.fetch_method === 'praw_fallback') {
              msg += ` (⚠️ PRAW fallback ~1000 cap. Direct API: ${r.direct_api_error || 'failed'})`;
            } else if (r.fetch_method === 'direct_api' && r.batches_fetched) {
              msg += ` (${r.batches_fetched} API batches)`;
            }
            setMessage({ type: 'success', text: msg });
            loadUserFlairs();
            break;
          }
          if (r && r.success === false) {
            const err = (r.error as string) || 'Unknown error';
            const tb = (r.traceback as string) || '';
            setMessage({
              type: 'error',
              text: `Sync failed: ${err}${tb ? `\n\n${tb.slice(-1200)}` : ''}`,
            });
            break;
          }
          if (last.progress?.phase === 'error' && last.progress.error) {
            setMessage({ type: 'error', text: `Sync failed: ${last.progress.error}` });
            break;
          }
          stallAfterIdle++;
          if (stallAfterIdle > 25) {
            setMessage({
              type: 'warning',
              text: 'Sync finished but the final result file was not read in time. Check the user_flair table and server logs.',
            });
            break;
          }
          await sleep(2000);
        }
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setMessage({
        type: 'error',
        text: `Failed to get user flairs: ${msg}. Check network and API URL.`,
      });
    } finally {
      window.clearInterval(progressTimer);
      userFlairRedditSyncInProgressRef.current = false;
      setIsGettingUserFlairs(false);
      setUserFlairSyncProgress(null);
    }
  };

  const loadUserFlairs = async () => {
    if (!subreddit || !user) return;
    if (userFlairRedditSyncInProgressRef.current) {
      return;
    }
    setIsLoadingUserFlairs(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const searchParam = userFlairSearch ? `&search=${encodeURIComponent(userFlairSearch)}` : '';
      // Load small sample first (20 rows) for instant display, then load more if needed
      const initialLimit = userFlairPage === 1 ? 20 : 50;
      const url = `${API_BASE_URL}/moderator/${subreddit}/user-flairs?page=${userFlairPage}&limit=${initialLimit}${searchParam}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch (e) {
          // Response is not JSON
          const text = await response.text();
          errorMessage = text || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      if (data.success !== false) {
        if (userFlairRedditSyncInProgressRef.current) {
          return;
        }
        const newUsers = data.users || [];
        if (userFlairPage === 1) {
          // First page: set initial data immediately
          setUserFlairs(newUsers);
          // Load remaining data in background
          if (data.pagination?.total > 20) {
            setTimeout(async () => {
              try {
                if (userFlairRedditSyncInProgressRef.current) {
                  return;
                }
                const bgUrl = `${API_BASE_URL}/moderator/${subreddit}/user-flairs?page=1&limit=50${searchParam}`;
                const bgResponse = await fetch(bgUrl, {
                  headers: { 'Authorization': `Bearer ${user.username}` },
                });
                if (bgResponse.ok) {
                  const bgData = await bgResponse.json();
                  if (bgData.success !== false && bgData.users) {
                    if (userFlairRedditSyncInProgressRef.current) {
                      return;
                    }
                    setUserFlairs(bgData.users);
                  }
                }
              } catch (e) {
                // Background load failed, that's okay - we already have initial data
              }
            }, 100);
          }
        } else {
          if (userFlairRedditSyncInProgressRef.current) {
            return;
          }
          setUserFlairs(newUsers);
        }
        setUserFlairTotal(data.pagination?.total || 0);
        setUserFlairTotalPages(data.pagination?.totalPages || 0);
      } else {
        throw new Error(data.error || 'Failed to load user flairs');
      }
    } catch (error) {
      console.error('Error loading user flairs:', error);
      setMessage({
        type: 'error',
        text: `Failed to load user flairs: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsLoadingUserFlairs(false);
    }
  };

  const pollUntilDone = async (
    progressUrl: string,
    onTick?: (body: any) => void
  ) => {
    const { API_BASE_URL } = await import('../config');
    for (let i = 0; i < 400; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      const pr = await fetch(`${API_BASE_URL}${progressUrl}`, {
        headers: { Authorization: `Bearer ${user!.username}` },
      });
      const body = await pr.json();
      onTick?.(body);
      if (!body.isRunning) return body;
    }
    return { isRunning: false, result: { success: false, error: 'Timed out waiting for job' } };
  };

  const loadFixQueueStatus = async () => {
    if (!subreddit || !user) return;
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/user-flairs/fix-queue`, {
        headers: { Authorization: `Bearer ${user.username}` },
      });
      if (response.ok) setFixQueueStatus(await response.json());
    } catch {
      /* ignore */
    }
  };

  const handleDrainFixes = async (now = true) => {
    if (!subreddit || !user) return;
    setIsDrainingFixes(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/user-flairs/drain-fixes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.username}`,
        },
        body: JSON.stringify({ now, maxBatches: 40 }),
      });
      const data = await response.json();
      if (!response.ok) {
        setMessage({ type: 'error', text: data.error || 'Failed to start drain' });
        return;
      }
      const done = await pollUntilDone(`/moderator/${subreddit}/user-flairs/drain-fixes-progress`);
      const r = done.result || {};
      setMessage({
        type: r.success === false ? 'error' : 'success',
        text: r.error
          ? `Drain failed: ${r.error}`
          : `Drain: ${r.pushed || 0} pushed, ${r.failed || 0} failed, ${r.batches || 0} batches${r.capped ? ' (hourly cap)' : ''}${r.paused ? ' (paused)' : ''}`,
      });
      await loadFixQueueStatus();
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Drain failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsDrainingFixes(false);
    }
  };

  const handleFixQueuePause = async (paused: boolean) => {
    if (!subreddit || !user) return;
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/user-flairs/fix-queue/pause`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.username}`,
        },
        body: JSON.stringify({ paused }),
      });
      if (response.ok) setFixQueueStatus(await response.json());
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to update pause: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleValidateUserFlairs = async (autoFixText: boolean = false, autoFixCss: boolean = false, autoFixTextMode: 'gentle' | 'robust' = 'gentle') => {
    if (!subreddit || !user) return;
    setIsValidating(true);
    setEnqueueProgress(null);
    try {
      const { API_BASE_URL } = await import('../config');
      if (!autoFixText && !autoFixCss) {
        const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/user-flairs/validate`, {
          headers: { Authorization: `Bearer ${user.username}` },
        });
        const data = await response.json();
        if (response.ok) {
          setValidationResults(data);
          setMessage({
            type: 'success',
            text: `Validation complete: ${data.users_with_issues} users with issues out of ${data.total_users} total`,
          });
        } else {
          setMessage({ type: 'error', text: `Failed to validate: ${data.error || 'Unknown error'}` });
        }
        return;
      }

      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/user-flairs/enqueue-fixes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.username}`,
        },
        body: JSON.stringify({ autoFixText, autoFixCss, autoFixTextMode }),
      });
      const started = await response.json();
      if (!response.ok) {
        setMessage({ type: 'error', text: started.error || 'Failed to start enqueue' });
        return;
      }
      const done = await pollUntilDone(
        `/moderator/${subreddit}/user-flairs/enqueue-fixes-progress`,
        (body) => setEnqueueProgress(body.progress)
      );
      const data = done.result || {};
      if (data.success === false) {
        setMessage({ type: 'error', text: data.error || 'Enqueue failed' });
        return;
      }
      setValidationResults(data);
      const messageParts = [
        `Queued fixes: ${data.enqueued || 0} users (${data.auto_fixed || 0} DB updates) of ${data.total_users || 0} scanned`,
      ];
      if (data.unable_to_fix?.length) {
        messageParts.push(`${data.unable_to_fix.length} need manual review`);
      }
      setMessage({ type: 'success', text: messageParts.join('. ') });
      if (data.auto_fixed > 0) loadUserFlairs();
      await loadFixQueueStatus();
      if ((data.enqueued || 0) > 0) {
        await handleDrainFixes(true);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to validate: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsValidating(false);
      setEnqueueProgress(null);
    }
  };

  const handleUpdateUserFlair = async (username: string, updates: any) => {
    if (!subreddit || !user) return;
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/user-flairs/${username}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.username}`,
        },
        body: JSON.stringify(updates),
      });
      
      const data = await response.json();
      if (response.ok) {
        setMessage({
          type: 'success',
          text: `User flair updated for ${username}`,
        });
        setEditingUserFlair(null);
        loadUserFlairs();
      } else {
        setMessage({
          type: 'error',
          text: `Failed to update: ${data.error || 'Unknown error'}`,
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handlePushUserFlairToReddit = async (username: string) => {
    if (!subreddit || !user) return;
    setIsPushingFlair(username);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/user-flairs/${username}/push-to-reddit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      
      const data = await response.json();
      if (response.ok) {
        setMessage({
          type: 'success',
          text: `Flair pushed to Reddit for ${username}`,
        });
      } else {
        setMessage({
          type: 'error',
          text: `Failed to push: ${data.error || 'Unknown error'}`,
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Failed to push: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsPushingFlair(null);
    }
  };

  const handleTableSave = async () => {
    if (!editingRow || !subreddit || !user || !activeTab || isSubscribed || isSaving) return;
    const tableName = activeTab; // activeTab is checked above, so it's guaranteed to be string
    setIsSaving(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/admin/tables/${tableName}?subreddit=${encodeURIComponent(subreddit)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.username}`,
        },
        body: JSON.stringify({
          subreddit: subreddit,
          row: editingRow,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: 'Table updated successfully!' });
        setEditingRow(null);
        setOriginalEditingRow(null);
        setEditFieldMetadata([]); // Clear metadata after save
        
        // Clear caches affected by the edit (defer heavy operations to avoid blocking UI)
        setTimeout(() => {
          if (originalEditingRow && editingRow) {
            // Check if name fields changed (these affect parent dropdowns and filtered views)
            const nameField = `${tableName}_name`;
            const oldName = originalEditingRow[nameField];
            const newName = editingRow[nameField];
            
            if (oldName !== newName) {
              // Name changed - clear all caches for this table and parent option caches
              setTableDataCache(prev => {
                const newCache = { ...prev };
                // Clear all cache entries for this table
                Object.keys(newCache).forEach(key => {
                  if (key.startsWith(`${subreddit}_${tableName}_`)) {
                    delete newCache[key];
                  }
                });
                return newCache;
              });
              
              // Clear parent options cache if this affects parent dropdowns
              if (tableName === 'genus' || tableName === 'family' || tableName === 'class') {
                setParentOptions(prev => ({
                  ...prev,
                  [tableName]: undefined // Force reload of parent options
                }));
              }
            }
            
            // Check if parent relationships changed (affects filtered views)
            const parentFields: { [key: string]: string[] } = {
              flair: ['genus_name'],
              genus: ['family_name'],
              family: ['class_name']
            };
            
            const fieldsToCheck = parentFields[tableName] || [];
            for (const field of fieldsToCheck) {
              const oldParent = originalEditingRow[field];
              const newParent = editingRow[field];
              
              if (oldParent !== newParent) {
                // Parent changed - clear cache for both old and new parent views
                const parentType = field.replace('_name', '');
                
                // Clear old parent cache
                if (oldParent) {
                  const oldFilters: { class?: string; family?: string; genus?: string } = {};
                  if (parentType === 'class') oldFilters.class = String(oldParent);
                  else if (parentType === 'family') oldFilters.family = String(oldParent);
                  else if (parentType === 'genus') oldFilters.genus = String(oldParent);
                  
                  const oldCacheKey = `${subreddit}_${tableName}_${JSON.stringify(oldFilters)}`;
                  setTableDataCache(prev => {
                    const newCache = { ...prev };
                    delete newCache[oldCacheKey];
                    return newCache;
                  });
                }
                
                // Clear new parent cache
                if (newParent) {
                  const newFilters: { class?: string; family?: string; genus?: string } = {};
                  if (parentType === 'class') newFilters.class = String(newParent);
                  else if (parentType === 'family') newFilters.family = String(newParent);
                  else if (parentType === 'genus') newFilters.genus = String(newParent);
                  
                  const newCacheKey = `${subreddit}_${tableName}_${JSON.stringify(newFilters)}`;
                  setTableDataCache(prev => {
                    const newCache = { ...prev };
                    delete newCache[newCacheKey];
                    return newCache;
                  });
                }
              }
            }
          }
          
          // Always clear current view cache and reload
          const currentFilters: { class?: string; family?: string; genus?: string } = {};
          if (selectedClass) currentFilters.class = selectedClass;
          if (selectedFamily) currentFilters.family = selectedFamily;
          if (selectedGenus) currentFilters.genus = selectedGenus;
          
          const currentCacheKey = `${subreddit}_${tableName}_${JSON.stringify(currentFilters)}`;
          setTableDataCache(prev => {
            const newCache = { ...prev };
            delete newCache[currentCacheKey];
            return newCache;
          });
          
          // Force reload with current filters
          loadTableData(tableName, currentFilters, true);
          // Skip heavy master sheet reload for image/flair (text edits) — invalidate cache; sheet reloads on Flair Sheet tab.
          if (tableName === 'image' || tableName === 'flair') {
            setMasterSheetCacheKey('');
          } else {
            loadMasterSheet(true);
          }
        }, 0);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update table' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      // Always reset saving state, even on error or early return
      setIsSaving(false);
    }
  };

  const handleImageUpload = async () => {
    if (selectedImages.length === 0 || !subreddit || !user) return;
    setIsUploadingImages(true);
    setUploadProgress({});
    setUploadedImageNames([]);
    
    try {
      const { API_BASE_URL } = await import('../config');
      const BATCH_SIZE = 50; // Process 50 images at a time
      const totalBatches = Math.ceil(selectedImages.length / BATCH_SIZE);
      let totalUploaded = 0;
      let totalErrors = 0;
      let totalIgnored = 0;
      const allUploadedImageNames: string[] = [];
      const allIgnoredImages: Array<{filename: string, imageName: string, reason: string}> = [];
      const allErrorItems: Array<{ filename?: string; error?: string; size?: string }> = [];
      
      // Process batches sequentially
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const start = batchIndex * BATCH_SIZE;
        const end = Math.min(start + BATCH_SIZE, selectedImages.length);
        const batch = selectedImages.slice(start, end);
        
        const formData = new FormData();
        batch.forEach((file) => {
          formData.append('images', file);
        });

        // Upload batch with progress tracking
        const batchResult = await new Promise<{
          uploaded: number;
          errors: number;
          ignored: number;
          imageNames: string[];
          ignoredImages: Array<{ filename: string; imageName: string; reason: string }>;
          errorItems: Array<{ filename?: string; error?: string; size?: string }>;
        }>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          let resolved = false;
          
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              // Calculate overall progress across all batches
              const batchProgress = (e.loaded / e.total) * 100;
              const overallProgress = ((batchIndex * BATCH_SIZE + (batch.length * batchProgress / 100)) / selectedImages.length) * 100;
              setUploadProgress({ 
                overall: Math.min(overallProgress, 99), // Cap at 99% until all batches complete
                currentBatch: batchIndex + 1,
                totalBatches: totalBatches,
                batchProgress: batchProgress
              });
            }
          });

          const handleResponse = () => {
            if (resolved) return;
            resolved = true;
            
            if (xhr.status === 200 || (xhr.status === 0 && xhr.readyState === 4)) {
              try {
                const responseText = xhr.responseText || '{}';
                const data = JSON.parse(responseText);
                const uploadedCount = data.results?.uploaded?.length || data.uploaded?.length || 0;
                const errorCount = data.results?.errors?.length || data.errors?.length || 0;
                const ignoredCount = data.results?.ignored?.length || data.ignored?.length || 0;
                const imageNames = (data.results?.uploaded || data.uploaded || []).map((item: any) => item.imageName || item.filename?.replace(/\.(png|jpg|jpeg)$/i, ''));
                const ignoredImages = data.results?.ignored || data.ignored || [];
                const errorItems = data.results?.errors || data.errors || [];
                if (errorItems.length > 0) {
                  console.error(`Import images batch ${batchIndex + 1} errors:`, errorItems);
                }
                if (ignoredImages.length > 0) {
                  console.warn(`Import images batch ${batchIndex + 1} ignored:`, ignoredImages);
                }
                resolve({
                  uploaded: uploadedCount,
                  errors: errorCount,
                  ignored: ignoredCount,
                  imageNames,
                  ignoredImages,
                  errorItems,
                });
              } catch (e) {
                const errorMessage = e instanceof Error ? e.message : String(e);
                const responsePreview = xhr.responseText?.substring(0, 500) || 'No response text';
                console.error(`Batch ${batchIndex + 1} parse error:`, e, responsePreview);
                reject(new Error(`Failed to parse response: ${errorMessage}`));
              }
            } else {
              try {
                const errorData = xhr.responseText ? JSON.parse(xhr.responseText) : {};
                console.error(`Batch ${batchIndex + 1} failed:`, errorData);
                reject(new Error(errorData.error || errorData.details || `Upload failed: ${xhr.statusText}`));
              } catch (e) {
                reject(new Error(`Upload failed with status ${xhr.status}`));
              }
            }
          };
          
          xhr.addEventListener('load', handleResponse);
          
          // Fallback: loadend fires after load/error/timeout
          xhr.addEventListener('loadend', () => {
            if (!resolved && xhr.readyState === 4) {
              handleResponse();
            }
          });

          xhr.addEventListener('error', (e) => {
            if (resolved) return;
            resolved = true;
            console.error(`Batch ${batchIndex + 1} network error:`, e);
            reject(new Error('Network error during upload'));
          });

          xhr.addEventListener('timeout', () => {
            if (resolved) return;
            resolved = true;
            console.error(`Batch ${batchIndex + 1} timeout`);
            reject(new Error('Upload timeout'));
          });
          
          xhr.addEventListener('abort', () => {
            if (resolved) return;
            resolved = true;
            console.error(`Batch ${batchIndex + 1} aborted`);
            reject(new Error('Upload aborted'));
          });

          xhr.timeout = 5 * 60 * 1000; // 5 minute timeout per batch
          xhr.open('POST', `${API_BASE_URL}/moderator/${subreddit}/import-images`);
          xhr.setRequestHeader('Authorization', `Bearer ${user.username}`);
          xhr.send(formData);
        });

        totalUploaded += batchResult.uploaded;
        totalErrors += batchResult.errors;
        totalIgnored += batchResult.ignored;
        allUploadedImageNames.push(...batchResult.imageNames);
        allIgnoredImages.push(...batchResult.ignoredImages);
        allErrorItems.push(...batchResult.errorItems);
        
        // Small delay between batches to avoid overwhelming the server
        if (batchIndex < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // Final progress update
      setUploadProgress({ overall: 100, currentBatch: totalBatches, totalBatches: totalBatches });
      
      if (totalErrors > 0) {
        const errorDetail = allErrorItems
          .slice(0, 12)
          .map((e) => `${e.filename || '?'}${e.size ? ` (${e.size})` : ''}: ${e.error || 'unknown'}`)
          .join('\n');
        const ignoredDetail =
          totalIgnored > 0
            ? `Ignored (rename to image_source/flair_code stem): ${allIgnoredImages.map((img) => img.filename || img.imageName).join(', ')}`
            : '';
        setMessage({
          type: 'error',
          text: `Uploaded ${totalUploaded} images, but ${totalErrors} errors occurred.${totalIgnored > 0 ? ` ${totalIgnored} ignored (not registered).` : ''}`,
          details: [errorDetail, ignoredDetail].filter(Boolean).join('\n\n') || undefined,
        });
        setIsUploadingImages(false);
        // Still show dialog if we have uploaded images despite errors
        if (allUploadedImageNames.length > 0) {
          setUploadedImageNames(allUploadedImageNames);
          setShowPushEmojisDialog(true);
        }
      } else if (totalIgnored > 0) {
        setUploadedImageNames(allUploadedImageNames);
        setMessage({
          type: 'warning',
          text: `Successfully uploaded ${totalUploaded} images in ${totalBatches} batches. ${totalIgnored} images ignored (not in image table for this subreddit).`,
          details: `Ignored: ${allIgnoredImages.map(img => img.imageName || img.filename).join(', ')}`
        });
        setIsUploadingImages(false);
        // Show dialog to push emojis if we have uploaded images
        if (allUploadedImageNames.length > 0) {
          setShowPushEmojisDialog(true);
        }
      } else {
        setUploadedImageNames(allUploadedImageNames);
        setMessage({
          type: 'success',
          text: `Successfully uploaded ${totalUploaded} images in ${totalBatches} batches!`,
        });
        setIsUploadingImages(false);
        // Show dialog to push emojis if we have uploaded images
        if (allUploadedImageNames.length > 0) {
          setShowPushEmojisDialog(true);
        }
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
      setIsUploadingImages(false);
      setUploadProgress({});
    }
  };

  const handleListUnusedImages = async () => {
    if (!subreddit || !user) return;
    setIsLoadingUnusedImages(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/list-unused-images`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setUnusedImages(data.unusedImages || []);
        if (data.unusedImages && data.unusedImages.length > 0) {
          setShowPurgeDialog(true);
        } else {
          // Show debug info if available
          const debugInfo = data.debug ? 
            `\n\nDebug info:\n- Registered images: ${data.registeredCount}\n- Holyfamily in registered: ${data.debug.holyfamilyCheck?.inRegistered}\n- Holyfamily in unused: ${data.debug.holyfamilyCheck?.inUnused}\n- Registered sample: ${data.debug.registeredSample?.slice(0, 10).join(', ')}` : '';
          setMessage({ 
            type: 'warning', 
            text: `No unused images found. All images in CDN are registered.${debugInfo}`,
            details: data.debug ? JSON.stringify(data.debug, null, 2) : undefined
          });
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to list unused images' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsLoadingUnusedImages(false);
    }
  };

  const handlePurgeUnusedImages = async () => {
    if (!subreddit || !user || unusedImages.length === 0) return;
    setIsPurgingImages(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/purge-unused-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.username}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageNames: unusedImages }),
      });
      const data = await response.json();
      if (data.success) {
        const re = data.redditEmoji;
        let extra = '';
        if (re && re.success !== false) {
          const nd = (re.emojis_deleted && re.emojis_deleted.length) || 0;
          const nn = (re.emojis_not_on_subreddit && re.emojis_not_on_subreddit.length) || 0;
          const ne = (re.emoji_errors && re.emoji_errors.length) || 0;
          extra = ` Reddit: removed ${nd} custom emoji(s); ${nn} name(s) had no matching emoji on the sub.`;
          if (ne > 0) extra += ` ${ne} emoji error(s) — check server logs.`;
        } else if (re && re.error) {
          extra = ` Reddit emoji cleanup failed: ${re.error}`;
        }
        setMessage({
          type: re && re.success === false && re.error ? 'warning' : 'success',
          text: `Successfully purged ${data.deletedCount || 0} image files (${unusedImages.length} images across all sizes).${extra}`,
        });
        setUnusedImages([]);
        setShowPurgeDialog(false);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to purge unused images' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsPurgingImages(false);
    }
  };

  const handleListMissingImages = async () => {
    if (!subreddit || !user) return;
    setIsLoadingMissingImages(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/list-missing-images`, {
        headers: {
          'Authorization': `Bearer ${user.username}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setMissingImages(data.missingImages || []);
        if (data.missingImages && data.missingImages.length > 0) {
          setShowMissingImagesDialog(true);
        } else {
          setMessage({ 
            type: 'success', 
            text: `All registered images exist in CDN. Total checked: ${data.totalRegistered || 0}` 
          });
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to list missing images' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsLoadingMissingImages(false);
    }
  };

  const handleRedditEmojiAudit = async () => {
    if (!subreddit || !user) return;
    setIsLoadingRedditEmojiAudit(true);
    setRedditEmojiAudit(null);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/reddit-emoji-audit`, {
        headers: {
          Authorization: `Bearer ${user.username}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setRedditEmojiAudit(data);
        const oc = data.orphan_count ?? (data.orphans?.length ?? 0);
        if (oc > 0) {
          setShowRedditEmojiPurgeDialog(true);
        } else {
          setMessage({
            type: 'success',
            text: `Reddit emojis match master_sheet: ${data.count_on_reddit ?? 0} on sub, ${data.master_sheet_flair_codes ?? 0} distinct flair_code(s) in master_sheet. No orphans.`,
          });
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to audit Reddit emojis' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsLoadingRedditEmojiAudit(false);
    }
  };

  const handlePurgeRedditEmojiOrphans = async () => {
    if (!subreddit || !user) return;
    const orphans = redditEmojiAudit?.orphans;
    if (!orphans || orphans.length === 0) return;
    setIsPurgingRedditEmojiOrphans(true);
    try {
      const { API_BASE_URL } = await import('../config');
      const response = await fetch(`${API_BASE_URL}/moderator/${subreddit}/purge-reddit-emoji-orphans`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user.username}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ names: orphans }),
      });
      const data = await response.json();
      if (data.success !== false) {
        const nd = (data.emojis_deleted && data.emojis_deleted.length) || 0;
        const ne = (data.emoji_errors && data.emoji_errors.length) || 0;
        setMessage({
          type: ne > 0 ? 'warning' : 'success',
          text: `Removed ${nd} custom emoji(s) from Reddit.${ne > 0 ? ` ${ne} error(s) — check server logs.` : ''}`,
        });
        setShowRedditEmojiPurgeDialog(false);
        setRedditEmojiAudit(null);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to purge Reddit emojis' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsPurgingRedditEmojiOrphans(false);
    }
  };

  if (authLoading || (user && (isLoading || isCheckingModerator))) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Flair Wizard Admin
            </Typography>
            <Button color="inherit" onClick={() => navigate('/')}>Login</Button>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error">You must be logged in to access the admin panel.</Alert>
        </Container>
      </Box>
    );
  }

  const canAccessAdmin =
    isSiteAdmin || (user.isModerator && user.moderatorSubreddit === subreddit);

  if (!canAccessAdmin) {
    return (
      <Box>
        <AppBar position="static" color="primary">
          <Toolbar>
            <RedditIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            /r/{canonicalSubredditName} Flair Admin
          </Typography>
            <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout}>Logout</Button>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error">
            You are not authorized to access the admin panel for /r/{canonicalSubredditName}.
          </Alert>
        </Container>
      </Box>
    );
  }

  // Navigation handlers for nested navigation
  const handleClassClick = (className: string) => {
    setSelectedClass(className);
    setSelectedFamily(null);
    setSelectedGenus(null);
    setSelectedFlair(null);
    setActiveTab('family');
  };

  const handleFamilyClick = (familyName: string) => {
    setSelectedFamily(familyName);
    setSelectedGenus(null);
    setSelectedFlair(null);
    setActiveTab('genus');
  };

  const handleGenusClick = (genusName: string) => {
    setSelectedGenus(genusName);
    setSelectedFlair(null);
    setActiveTab('flair');
  };



  const adminContextValue = {
    subreddit: subreddit ?? null,
    canonicalSubredditName,
    user,
    activeTab,
    setActiveTab,
    activeSettingsTab,
    setActiveSettingsTab,
    message,
    setMessage,
    clearDataDialogOpen,
    setClearDataDialogOpen,
    isClearingData,
    tableData,
    isLoadingTable,
    setTableData,
    tableDataCache,
    setTableDataCache,
    selectedClass,
    setSelectedClass,
    selectedFamily,
    setSelectedFamily,
    selectedGenus,
    setSelectedGenus,
    selectedFlair,
    setSelectedFlair,
    parentOptions,
    setParentOptions,
    loadTableData,
    isDeletingItem,
    setIsDeletingItem,
    selectedFile,
    setSelectedFile,
    isUploading,
    handleAppendCsvUpload,
    selectedHistoryFiles,
    setSelectedHistoryFiles,
    isImportingHistory,
    handleImportHistory,
    selectedGenusCsvFile,
    setSelectedGenusCsvFile,
    isImportingGenusChanges,
    handleImportGenusChanges,
    selectedFadingCsvFile,
    setSelectedFadingCsvFile,
    isImportingFading,
    handleImportFading,
    fadedAssetsClassInput,
    setFadedAssetsClassInput,
    isGeneratingFadedAssets,
    handleGenerateFadedAssetsForClass,
    fadeGenusInput,
    setFadeGenusInput,
    fadeGenusIncludeBandwagons,
    setFadeGenusIncludeBandwagons,
    isFadingGenus,
    fadeGenusPreview,
    handleFadeGenus,
    handleSyncMasterSheetFromImages,
    handleRebuildMasterSheetFromBaseTables,
    isSyncingMasterSheet,
    selectedImages,
    setSelectedImages,
    isUploadingImages,
    handleImageUpload,
    uploadProgress,
    handleListUnusedImages,
    isLoadingUnusedImages,
    isPurgingImages,
    handleListMissingImages,
    isLoadingMissingImages,
    showMissingImagesDialog,
    setShowMissingImagesDialog,
    missingImages,
    showPurgeDialog,
    setShowPurgeDialog,
    unusedImages,
    handlePurgeUnusedImages,
    redditEmojiAudit,
    setRedditEmojiAudit,
    showRedditEmojiPurgeDialog,
    setShowRedditEmojiPurgeDialog,
    isLoadingRedditEmojiAudit,
    isPurgingRedditEmojiOrphans,
    handleRedditEmojiAudit,
    handlePurgeRedditEmojiOrphans,
    forceEmojiImageNames,
    setForceEmojiImageNames,
    handleForceEmojiUpdate,
    isPushingForceEmojis,
    cropToSidesOptions,
    cropToSidesSelected,
    cropToSidesCdnStem,
    cropToSidesPreviewBust,
    isLoadingCropToSidesOptions,
    isResolvingCropToSidesStem,
    isCroppingToSides,
    handleLoadCropToSidesOptions,
    handleCropToSidesSelectionChange,
    handleCropImageToSidesConfirm,
    redditLimits,
    subscriptions,
    handleUnsubscribe,
    selectedParentSub,
    setSelectedParentSub,
    availableSubs,
    isLoadingSubscriptions,
    handleSubscribe,
    isSubscribed,
    masterSheetSourceSub,
    flairSheetCdnSub,
    hasAuth,
    handleInitiateRedditAuth,
    isAuthingReddit,
    handlePushWikiPages,
    isPushingWiki,
    handleFixWikiPages,
    isFixingWikiPages,
    wikiPushStatus,
    wikiFixStatus,
    handleDiscordAuth,
    isConnectingDiscord,
    discordConnected,
    handleDownloadOldSheet,
    handleUpdateDefaultFlair,
    isUpdatingDefaultFlair,
    updateDefaultFlairProgress,
    handleImportModerators,
    isImportingModerators,
    handleSyncSheets,
    isSyncingSheets,
    handleGetUserFlairs,
    isGettingUserFlairs,
    userFlairSyncProgress,
    handleValidateUserFlairs,
    isValidating,
    validationResults,
    setValidationResults,
    fixQueueStatus,
    enqueueProgress,
    loadFixQueueStatus,
    handleDrainFixes,
    handleFixQueuePause,
    isDrainingFixes,
    userFlairSearch,
    setUserFlairSearch,
    setUserFlairPage,
    isLoadingUserFlairs,
    userFlairs,
    editingUserFlair,
    setEditingUserFlair,
    handleUpdateUserFlair,
    handlePushUserFlairToReddit,
    isPushingFlair,
    userFlairPage,
    userFlairTotalPages,
    userFlairTotal,
    isLoadingScheduler,
    schedulerSettings,
    handleUpdateSchedulerSettings,
    isUpdatingScheduler,
    subredditSettings,
    loadSubredditSettings,
    handleUpdateSubredditSettings,
    isLoadingSubredditSettings,
    isUpdatingSubredditSettings,
    handleRunNightlyJobs,
    isRunningNightlyJobs,
    loadNightlyJobHistory,
    isLoadingHistory,
    nightlyJobHistory,
    isLoadingAwards,
    awards,
    selectedAwardCode,
    setSelectedAwardCode,
    setNewAwardUsername,
    newAwardUsername,
    handleRemoveAward,
    handleAssignAward,
    CDN_BASE_URL,
    masterSheetData,
    isLoadingMasterSheet,
    getColumnWidth,
    editingRow,
    setEditingRow,
    originalEditingRow,
    setOriginalEditingRow,
    editFieldMetadata,
    setEditFieldMetadata,
    isSaving,
    setIsSaving,
    handleTableSave,
    moveDialogOpen,
    setMoveDialogOpen,
    itemToMove,
    setItemToMove,
    moveTargetOptions,
    setMoveTargetOptions,
    selectedMoveTarget,
    setSelectedMoveTarget,
    isMoving,
    handleConfirmMove,
    showPushEmojisDialog,
    setShowPushEmojisDialog,
    uploadedImageNames,
    setUploadedImageNames,
    isPushingEmojis,
    handlePushEmojisAfterUpload,
    isSiteAdmin,
  };

  return (
    <AdminContextProvider value={adminContextValue}>
    <Box>
      <HeaderBar
        canonicalSubredditName={canonicalSubredditName}
        username={user?.username ?? ''}
        onBack={() =>
          navigateOrExternal(
            navigate,
            rawSubreddit ? subredditLandingUrl(canonicalSubredditName) : '/'
          )
        }
        onLogout={logout}
      />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
            Admin Panel for /r/{canonicalSubredditName}
          </Typography>

        {isSiteAdmin && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Signed in as site admin — full access to this subreddit&apos;s panel (same as a mod). Configure accounts in{' '}
            <code>SITE_ADMIN_USERNAMES</code> on the server.
          </Alert>
        )}

        {/* Sub Settings Bar - Always visible at top */}
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
            <Typography variant="h6">Sub Settings</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {hasAuth === false && (
                <Chip 
                  label="No Reddit Authorization" 
                  color="warning" 
                  size="small"
                  sx={{ mr: 1 }}
                />
              )}
              {isSubscribed && (
                <Chip 
                  label={`Subscribed to: ${subscriptions.join(', ')}`} 
                  color="info" 
                  size="small"
                />
              )}
              <Button
                variant="outlined"
                onClick={() => setActiveSettingsTab('settings')}
                size="small"
              >
                Manage Settings
            </Button>
          </Box>
        </Box>
        </Paper>


        {/* Table Management Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={activeTab || false}
            onChange={(_e, newValue) => {
              setActiveTab(newValue);
              setActiveSettingsTab(null);
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Flair Sheet" value="flair-sheet" />
            {!isSubscribed && <Tab label="Class" value="class" />}
            {!isSubscribed && <Tab label="Family" value="family" />}
            {!isSubscribed && <Tab label="Genus" value="genus" />}
            {!isSubscribed && <Tab label="Flair" value="flair" />}
            {!isSubscribed && <Tab label="Image" value="image" />}
          </Tabs>
        </Paper>

        {/* Settings & Import Tabs */}
        {!isSubscribed && (
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeSettingsTab || false}
              onChange={(_e, newValue: 'csv' | 'images' | 'spritesheets' | 'settings' | 'user-flair' | 'grant-user-flair' | 'awards' | 'scheduler') => {
                setActiveSettingsTab(newValue);
                setActiveTab(null);
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="CSV Import" value="csv" />
              <Tab label="Import Images" value="images" />
              <Tab label="Spritesheets" value="spritesheets" />
              <Tab label="User Flair Management" value="user-flair" />
              <Tab label="Grant User Flair" value="grant-user-flair" />
              <Tab label="Scheduler" value="scheduler" />
              <Tab label="Awards" value="awards" />
            </Tabs>
          </Paper>
        )}

        {/* Subscribed subs: tools that work with parent CDN / nightly sync (no CSV or image import) */}
        {isSubscribed && (
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeSettingsTab || false}
              onChange={(_e, newValue: 'user-flair' | 'grant-user-flair' | 'spritesheets' | 'awards' | 'scheduler' | 'settings') => {
                setActiveSettingsTab(newValue);
                setActiveTab(null);
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="User Flair Management" value="user-flair" />
              <Tab label="Grant User Flair" value="grant-user-flair" />
              <Tab label="Spritesheets" value="spritesheets" />
              <Tab label="Scheduler" value="scheduler" />
              <Tab label="Awards" value="awards" />
              <Tab label="Manage Settings" value="settings" />
            </Tabs>
          </Paper>
        )}

        {/* Only show content from one tab bar at a time */}
        {activeTab && !activeSettingsTab && (
          <>
            {/* Flair Sheet View Tab */}
            {activeTab === 'flair-sheet' && (
              <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
                  Flair Sheet View (read only)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {isSubscribed && masterSheetSourceSub ? (
                    <>
                      Read-only copy of the flair sheet from <strong>/r/{masterSheetSourceSub}</strong> (this sub
                      subscribes to that community for flair data). Thumbnails use that sub&apos;s CDN paths.
                    </>
                  ) : (
                    <>View all flair entries in the flair sheet for /r/{canonicalSubredditName}</>
                  )}
          </Typography>
                <FlairSheetSection />
          </Paper>
            )}

            {/* Editable Table Tabs */}
            {['class', 'family', 'genus', 'flair', 'image'].includes(activeTab) && (
              <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
                  {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Table
          </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Edit {activeTab} entries. Changes will be reflected in both the base table and master_sheet.
          </Typography>
                <TableSection
                  tableName={activeTab}
                  handleTableEdit={handleTableEdit}
                  handleMoveItem={handleMoveItem}
                  handleDeleteItem={handleDeleteItem}
                  handleAddItem={handleAddItem}
                  handleClassClick={handleClassClick}
                  handleFamilyClick={handleFamilyClick}
                  handleGenusClick={handleGenusClick}
                  newItemData={newItemData}
                  setNewItemData={setNewItemData}
                  isAddingItem={isAddingItem}
                />
              </Paper>
            )}
          </>
        )}

        {activeSettingsTab && <SettingsTabs />}
      </Container>

      <PageDialogs />
      <ClearDataDialog
        open={clearDataDialogOpen}
        subreddit={subreddit ?? null}
        isClearing={isClearingData}
        onClose={() => setClearDataDialogOpen(false)}
        onConfirm={handleClearData}
      />
      <MessageSnackbar message={message} onClose={() => setMessage(null)} />
    </Box>
    </AdminContextProvider>
  );
}
) as unknown as React.FC;

export default AdminPage;
